'use client';

import {
  useCallback,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { EditableContent } from '../../../edit/EditableContent';
import { EditableMedia } from '../../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../../lib/responsiveImageSizes';
import { pickChrome } from '../../../lib/siteChromeCopy';
import type { BlockRenderProps } from '../../../types';
import { MenuItemDietaryTags, MenuItemName } from '../MenuItemTags';
import { isMenuItemActive, resolveItemImageUrl } from '../menuEditHelpers';
import { readHighlightMenuSection } from './highlightMenuItems';

const SWIPE_THRESHOLD_PX = 36;

/** Overlapping polaroid-style stack — playful café / bistro energy; tap/swipe/marks to cycle. */
export function MenuHighlightZine({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const { sectionTitle, sectionSubtitle, dishes } = readHighlightMenuSection(
    block,
    locale,
  );
  const target = isActive ? activeEditTarget : null;
  const stack = dishes.slice(0, 5);
  const interactive = !editable && stack.length > 1;
  const [front, setFront] = useState(0);
  const pointerStartX = useRef<number | null>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const marksNavLabel = pickChrome('highlightZineNav', locale);
  const goToCardLabel = pickChrome('highlightZineGoTo', locale);

  const focusStack = useCallback(() => {
    stackRef.current?.focus({ preventScroll: true });
  }, []);

  const goTo = useCallback(
    (index: number) => {
      if (stack.length < 2) return;
      setFront(((index % stack.length) + stack.length) % stack.length);
    },
    [stack.length],
  );

  const advance = useCallback(() => {
    if (stack.length < 2) return;
    setFront((current) => (current + 1) % stack.length);
  }, [stack.length]);

  const retreat = useCallback(() => {
    if (stack.length < 2) return;
    setFront((current) => (current - 1 + stack.length) % stack.length);
  }, [stack.length]);

  const visualIndex = (index: number) =>
    stack.length === 0 ? 0 : (index - front + stack.length) % stack.length;

  const cardIndexFromEvent = (event: PointerEvent<HTMLDivElement>) => {
    const card = (event.target as Element | null)?.closest?.('.ws-hl-zine__card');
    if (!(card instanceof HTMLElement)) return null;
    const raw = card.getAttribute('data-zine-index');
    if (raw == null) return null;
    const index = Number(raw);
    return Number.isFinite(index) ? index : null;
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!interactive) return;
    pointerStartX.current = event.clientX;
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!interactive) return;
    const startX = pointerStartX.current;
    pointerStartX.current = null;
    if (startX == null) return;
    const delta = event.clientX - startX;
    if (Math.abs(delta) >= SWIPE_THRESHOLD_PX) {
      if (delta < 0) advance();
      else retreat();
      return;
    }
    // Stationary tap — bring the clicked card forward (front tap still cycles).
    if (Math.abs(delta) <= 8) {
      const tapped = cardIndexFromEvent(event);
      if (tapped != null && tapped !== front) {
        goTo(tapped);
        return;
      }
      advance();
    }
  };

  const onPointerCancel = () => {
    pointerStartX.current = null;
  };

  const onBrowseKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!interactive) return;
    if (event.key === 'ArrowRight' || event.key === 'Enter' || event.key === ' ') {
      // Space/Enter on mark buttons should activate the button, not double-step.
      const targetEl = event.target as HTMLElement | null;
      if (
        (event.key === 'Enter' || event.key === ' ') &&
        targetEl?.closest?.('button')
      ) {
        return;
      }
      event.preventDefault();
      advance();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      retreat();
    }
  };

  return (
    <section
      className={['ws-hl-zine', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="menu"
    >
      <div className="ws-hl-zine__inner">
        <header className="ws-hl-zine__intro">
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-hl-zine__title"
          >
            {sectionTitle}
          </EditableContent>
          {sectionSubtitle || editable ? (
            <EditableContent
              as="p"
              fieldKey="sectionSubtitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-hl-zine__subtitle"
            >
              {sectionSubtitle}
            </EditableContent>
          ) : null}
        </header>

        <div className="ws-hl-zine__stage">
        <div
          ref={stackRef}
          className="ws-hl-zine__stack"
          role="list"
          data-interactive={interactive ? 'true' : undefined}
          tabIndex={interactive ? 0 : undefined}
          aria-label={
            interactive
              ? `${sectionTitle || marksNavLabel}. ${front + 1} / ${stack.length}`
              : sectionTitle || undefined
          }
          style={
            {
              '--ws-hl-zine-depth': String(Math.max(0, stack.length - 1)),
            } as CSSProperties
          }
          onPointerDown={interactive ? onPointerDown : undefined}
          onPointerUp={interactive ? onPointerUp : undefined}
          onPointerCancel={interactive ? onPointerCancel : undefined}
          onKeyDown={interactive ? onBrowseKeyDown : undefined}
        >
          <div className="ws-hl-zine__fan">
          {stack.map(({ item, columnIndex, itemIndex }, index) => {
            const imageUrl = resolveItemImageUrl(block, item, columnIndex, itemIndex);
            const imageSrc = imageUrl ? buildSiteImageProxyUrl(imageUrl, 720, 78) : undefined;
            const imageSrcSet = imageUrl
              ? buildSiteImageSrcSet(imageUrl, [320, 480, 640, 900], 78)
              : undefined;
            const layer = visualIndex(index);
            const isFront = layer === 0;
            return (
              <article
                key={`${columnIndex}-${itemIndex}`}
                role="listitem"
                className={[
                  'ws-hl-zine__card',
                  isFront ? 'ws-hl-zine__card--front' : '',
                  isMenuItemActive(
                    target?.kind === 'menu-item' ? target : null,
                    columnIndex,
                    itemIndex,
                  )
                    ? 'ws-edit-child--active'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                data-zine-index={index}
                style={{ '--ws-hl-zine-i': String(layer) } as CSSProperties}
                aria-hidden={interactive && !isFront ? true : undefined}
                {...(editable
                  ? {
                      'data-edit-child': 'menu-item' as const,
                      'data-column-index': columnIndex,
                      'data-item-index': itemIndex,
                    }
                  : {})}
              >
                <div className="ws-hl-zine__media">
                  {imageUrl ? (
                    <EditableMedia
                      fieldKey={`itemImage_${columnIndex}_${itemIndex}`}
                      editable={editable}
                      activeEditTarget={activeEditTarget}
                      className="ws-hl-zine__media-frame"
                      src={imageSrc}
                      srcSet={imageSrcSet}
                      sizes={SITE_IMAGE_SIZES.menuHighlightZine}
                      alt={item.name || 'Dish'}
                      width={720}
                      height={720}
                      loading={isFront ? 'eager' : 'lazy'}
                      decoding="async"
                    />
                  ) : (
                    <div className="ws-hl-zine__media-fallback" aria-hidden />
                  )}
                </div>
                <div className="ws-hl-zine__caption">
                  <div className="ws-hl-zine__head">
                    <MenuItemName
                      item={item}
                      locale={locale}
                      className="ws-hl-zine__name"
                      showLegalMarks={false}
                      showOrderCode={false}
                    />
                    <MenuItemDietaryTags
                      item={item}
                      locale={locale}
                      iconsOnly
                      placement="inline"
                    />
                  </div>
                  <p className="ws-hl-zine__desc">{item.description}</p>
                </div>
              </article>
            );
          })}
          </div>
        </div>
        {interactive ? (
          <nav
            className="ws-hl-zine__marks"
            aria-label={marksNavLabel}
            onKeyDown={onBrowseKeyDown}
          >
            {stack.map(({ columnIndex, itemIndex }, index) => (
              <button
                key={`mark-${columnIndex}-${itemIndex}`}
                type="button"
                className="ws-hl-zine__mark"
                aria-label={goToCardLabel.replace('{n}', String(index + 1))}
                aria-current={index === front ? 'true' : undefined}
                onClick={(event) => {
                  event.stopPropagation();
                  goTo(index);
                  focusStack();
                }}
                onPointerDown={(event) => event.stopPropagation()}
                onPointerUp={(event) => event.stopPropagation()}
              />
            ))}
          </nav>
        ) : null}
        </div>
      </div>
    </section>
  );
}
